import axios from "axios";
import { getAccessToken } from "./authSession";
import { API_BASE_URL } from "../config";

const getAuthHeaders = () => {
    const token = getAccessToken();
    if (!token) {
        window.location.href = "/";
        return {};
    }
    return {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
};

const baseUrl = `${API_BASE_URL}/integrations/marketplaces/prom`;

export const getPromAccounts = async () => {
    const response = await axios.get(`${baseUrl}/accounts`, getAuthHeaders());
    return response.data;
};

export const createPromAccount = async (payload) => {
    const response = await axios.post(`${baseUrl}/accounts`, payload, getAuthHeaders());
    return response.data;
};

export const updatePromAccount = async (accountId, payload) => {
    const response = await axios.put(`${baseUrl}/accounts/${accountId}`, payload, getAuthHeaders());
    return response.data;
};

export const getPromSupplierSettings = async (accountId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/supplier-settings`, getAuthHeaders());
    return response.data;
};

export const updatePromSupplierSettings = async (accountId, supplierCode, payload) => {
    const response = await axios.put(
        `${baseUrl}/accounts/${accountId}/supplier-settings/${encodeURIComponent(supplierCode)}`,
        payload,
        getAuthHeaders(),
    );
    return response.data;
};

export const uploadPromProductScope = async (accountId, file, limit) => {
    const form = new FormData();
    form.append("file", file);
    const response = await axios.post(
        `${baseUrl}/accounts/${accountId}/scope/upload`,
        form,
        {
            ...getAuthHeaders(),
            headers: {
                ...getAuthHeaders().headers,
                "Content-Type": "multipart/form-data",
            },
            params: limit ? { limit } : {},
            timeout: 2 * 60 * 1000,
        },
    );
    return response.data;
};

export const clearPromProductScope = async (accountId) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/scope/clear`, {}, getAuthHeaders());
    return response.data;
};

export const getPromCategoryMappings = async (accountId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/category-mappings`, getAuthHeaders());
    return response.data;
};

export const upsertPromCategoryMapping = async (accountId, payload) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/category-mappings`, payload, getAuthHeaders());
    return response.data;
};

export const syncPromGroups = async (accountId) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/groups/sync`, {}, {
        ...getAuthHeaders(),
        timeout: 30 * 1000,
    });
    return response.data;
};

export const getPromInternalCategories = async (accountId, params = {}) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/internal-categories`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

export const getPromPlatformCategories = async (accountId, params = {}) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/platform-categories`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

export const syncPromPlatformCategories = async (accountId) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/platform-categories/sync`, {}, {
        ...getAuthHeaders(),
        timeout: 60 * 1000,
    });
    return response.data;
};

export const uploadPromPlatformCategories = async (accountId, file) => {
    const form = new FormData();
    form.append("file", file);
    const response = await axios.post(
        `${baseUrl}/accounts/${accountId}/platform-categories/import`,
        form,
        {
            ...getAuthHeaders(),
            headers: {
                ...getAuthHeaders().headers,
                "Content-Type": "multipart/form-data",
            },
            timeout: 2 * 60 * 1000,
        },
    );
    return response.data;
};

export const suggestPromPlatformCategories = async (accountId, internalCategoryCode, limit = 20) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/platform-categories/suggest`, {
        ...getAuthHeaders(),
        params: {
            internal_category_code: internalCategoryCode,
            limit,
        },
    });
    return response.data;
};

export const upsertPromPlatformCategory = async (accountId, payload) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/platform-categories`, payload, getAuthHeaders());
    return response.data;
};

export const markPromPlatformCategoryQuick = async (accountId, categoryId) => {
    const response = await axios.post(
        `${baseUrl}/accounts/${accountId}/platform-categories/${encodeURIComponent(categoryId)}/quick`,
        {},
        getAuthHeaders(),
    );
    return response.data;
};

export const unmarkPromPlatformCategoryQuick = async (accountId, categoryId) => {
    const response = await axios.delete(
        `${baseUrl}/accounts/${accountId}/platform-categories/${encodeURIComponent(categoryId)}/quick`,
        getAuthHeaders(),
    );
    return response.data;
};

export const uploadPromAttributeSchema = async (accountId, categoryId, file) => {
    const form = new FormData();
    form.append("file", file);
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/attribute-schema/${categoryId}/import`, form, {
        ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }, timeout: 2 * 60 * 1000,
    });
    return response.data;
};

export const getPromAttributeSchema = async (accountId, categoryId) => (await axios.get(`${baseUrl}/accounts/${accountId}/attribute-schema/${categoryId}`, getAuthHeaders())).data;
export const getPromAttributeFeedFields = async (accountId, categoryId) => (await axios.get(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/feed-fields`, getAuthHeaders())).data;
export const suggestPromAttributeMappings = async (accountId, categoryId) => (await axios.post(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/suggest`, {}, getAuthHeaders())).data;
export const approveSuggestedPromAttributeMappings = async (accountId, categoryId, kind) => (await axios.post(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/bulk/approve-suggested`, {}, { ...getAuthHeaders(), params: { kind } })).data;
export const getPromAttributeMappings = async (accountId, categoryId) => (await axios.get(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}`, getAuthHeaders())).data;
export const createPromAttributeMapping = async (accountId, categoryId, data) => (await axios.post(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}`, data, getAuthHeaders())).data;
export const updatePromAttributeMapping = async (accountId, categoryId, mappingId, data) => (await axios.patch(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/${mappingId}`, data, getAuthHeaders())).data;
export const getPromAttributeValueMappings = async (accountId, categoryId, mappingId) => (await axios.get(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/${mappingId}/values`, getAuthHeaders())).data;
export const createPromAttributeValueMapping = async (accountId, categoryId, mappingId, data) => (await axios.post(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/${mappingId}/values`, data, getAuthHeaders())).data;
export const updatePromAttributeValueMapping = async (accountId, categoryId, mappingId, valueId, data) => (await axios.patch(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/${mappingId}/values/${valueId}`, data, getAuthHeaders())).data;
export const getPromAttributeMappingSummary = async (accountId, categoryId) => (await axios.get(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/summary/data`, getAuthHeaders())).data;

export const generatePromFeed = async (accountId, payload) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/feed/generate`, payload, {
        ...getAuthHeaders(),
        timeout: 10 * 60 * 1000,
    });
    return response.data;
};

export const syncPromProductLinks = async (accountId, payload = {}) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/products/sync-links`, payload, {
        ...getAuthHeaders(),
        timeout: 10 * 60 * 1000,
    });
    return response.data;
};

export const syncPromStockPrices = async (accountId, payload = {}) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/stock-prices/sync`, payload, {
        ...getAuthHeaders(),
        timeout: 10 * 60 * 1000,
    });
    return response.data;
};

export const getPromJobs = async (accountId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/jobs`, getAuthHeaders());
    return response.data;
};

export const downloadPromSkippedReport = async (accountId, jobId, filename = "prom_skipped_report.xlsx") => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/jobs/${jobId}/skipped-report`, {
        ...getAuthHeaders(),
        responseType: "blob",
    });
    const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], {
            type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
    );
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
};

export const getPromRegions = async (search = "") => (await axios.get(`${baseUrl}/regions`, { ...getAuthHeaders(), params: { search, limit: 100 } })).data;
export const getPromSupplierRegions = async (accountId) => (await axios.get(`${baseUrl}/accounts/${accountId}/supplier-regions`, getAuthHeaders())).data;
export const updatePromSupplierRegions = async (accountId, supplierCode, regionIds) => (
    await axios.put(`${baseUrl}/accounts/${accountId}/supplier-regions/${encodeURIComponent(supplierCode)}`, { region_ids: regionIds }, getAuthHeaders())
).data;

// --- Order intake (Prom → SalesDrive) ---

export const getMarketplaceOrders = async (accountId, limit = 50) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/orders`, {
        ...getAuthHeaders(),
        params: { limit },
    });
    return response.data;
};

export const healthcheckMarketplaceOrders = async (accountId) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/orders/healthcheck`, {}, getAuthHeaders());
    return response.data;
};

export const dryRunMarketplaceOrders = async (accountId, limit = 10) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/orders/dry-run`, {}, {
        ...getAuthHeaders(),
        params: { limit },
        timeout: 2 * 60 * 1000,
    });
    return response.data;
};

export const retryMarketplaceOrder = async (orderId) => {
    const response = await axios.post(`${baseUrl}/orders/${orderId}/retry`, {}, getAuthHeaders());
    return response.data;
};

export const markMarketplaceOrderResolved = async (orderId) => {
    const response = await axios.post(`${baseUrl}/orders/${orderId}/mark-resolved`, {}, getAuthHeaders());
    return response.data;
};

// --- Outbound (статусы/ТТН в сторону Prom) ---

export const getMarketplaceStatusOutbox = async (accountId, limit = 50) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/status-outbox`, {
        ...getAuthHeaders(),
        params: { limit },
    });
    return response.data;
};

export const retryMarketplaceStatusOutbox = async (itemId) => {
    const response = await axios.post(`${baseUrl}/status-outbox/${itemId}/retry`, {}, getAuthHeaders());
    return response.data;
};

export const killMarketplaceStatusOutbox = async (itemId) => {
    const response = await axios.post(`${baseUrl}/status-outbox/${itemId}/dead`, {}, getAuthHeaders());
    return response.data;
};
